import React, { useState, useEffect, useCallback } from 'react';
import { Bell, Utensils, Droplets, Flame, Award, Users, Clock, Save, TestTube } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const notifTypes = [
  { key: 'meal_breakfast', label: 'Breakfast Reminder', icon: Utensils, color: 'text-amber-400', desc: 'Daily reminder to log breakfast', timeKey: 'breakfast_time' },
  { key: 'meal_lunch', label: 'Lunch Reminder', icon: Utensils, color: 'text-orange-400', desc: 'Daily reminder to log lunch', timeKey: 'lunch_time' },
  { key: 'meal_dinner', label: 'Dinner Reminder', icon: Utensils, color: 'text-red-400', desc: 'Daily reminder to log dinner', timeKey: 'dinner_time' },
  { key: 'streak_alerts', label: 'Streak Alerts', icon: Flame, color: 'text-orange-500', desc: "Alert when you're about to lose your streak" },
  { key: 'badges', label: 'Badge Notifications', icon: Award, color: 'text-yellow-400', desc: 'When you earn a new badge or achievement' },
  { key: 'water_reminders', label: 'Water Reminders', icon: Droplets, color: 'text-blue-400', desc: 'Regular hydration reminders throughout the day' },
  { key: 'weekly_summary', label: 'Weekly Summary', icon: Bell, color: 'text-indigo-400', desc: 'Weekly progress summary notification' },
  { key: 'friend_activity', label: 'Friend Activity', icon: Users, color: 'text-purple-400', desc: 'Likes, comments, and challenge invites' },
];

export default function PushNotificationPreferences({ token }) {
  const { t } = useTranslation();
  const [prefs, setPrefs] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const fetchPrefs = useCallback(async () => {
    try {
      const res = await fetch(`${API}/push/preferences`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPrefs(data);
      }
    } catch (e) { /* silent */ }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchPrefs(); }, [fetchPrefs]);

  const savePrefs = async () => {
    setSaving(true);
    try {
      await fetch(`${API}/push/preferences`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(prefs)
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) { /* silent */ }
    setSaving(false);
  };

  const sendTest = async () => {
    try {
      await fetch(`${API}/push/test`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (e) { /* silent */ }
  };

  const toggle = (key) => {
    setPrefs(p => ({ ...p, [key]: !p[key] }));
  };

  const updateTime = (key, value) => {
    setPrefs(p => ({ ...p, [key]: value }));
  };

  if (loading) {
    return <div className="animate-pulse h-40 bg-gray-100 dark:bg-gray-800/50 rounded-xl" />;
  }

  return (
    <div className="space-y-4" data-testid="push-notification-preferences">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Bell className="w-5 h-5 text-orange-400" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Push Notifications</h3>
        </div>
        <button
          onClick={sendTest}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700 dark:border-gray-600 rounded-lg text-xs text-gray-600 dark:text-gray-300 transition-colors"
          data-testid="test-push-btn"
        >
          <TestTube className="w-3.5 h-3.5" /> Test
        </button>
      </div>

      <div className="space-y-2">
        {notifTypes.map(({ key, label, icon: Icon, color, desc, timeKey }) => (
          <div key={key} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-900 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700/50">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <Icon className={`w-4 h-4 flex-shrink-0 ${color}`} />
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-white">{label}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{desc}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              {timeKey && prefs[key] && (
                <div className="flex items-center gap-1.5">
                  <Clock className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500 dark:text-gray-400" />
                  <input
                    type="time"
                    value={prefs[timeKey] || '07:00'}
                    onChange={(e) => updateTime(timeKey, e.target.value)}
                    className="bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-xs text-gray-900 dark:text-white w-24"
                    data-testid={`time-${timeKey}`}
                  />
                </div>
              )}
              <button
                onClick={() => toggle(key)}
                className={`relative w-11 h-6 rounded-full transition-colors ${prefs[key] ? 'bg-orange-500' : 'bg-gray-300 dark:bg-gray-600'}`}
                data-testid={`toggle-${key}`}
              >
                <span className={`absolute top-0.5 w-5 h-5 bg-white dark:bg-gray-800 rounded-full shadow transition-transform ${prefs[key] ? 'left-[22px]' : 'left-0.5'}`} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Water reminder interval */}
      {prefs.water_reminders && (
        <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-900 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700/50">
          <Droplets className="w-4 h-4 text-blue-400" />
          <span className="text-sm text-gray-900 dark:text-white">Remind every</span>
          <select
            value={prefs.water_interval_hours || 2}
            onChange={(e) => updateTime('water_interval_hours', parseInt(e.target.value))}
            className="bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm text-gray-900 dark:text-white"
            data-testid="water-interval-select"
          >
            <option value={1}>1 hour</option>
            <option value={2}>2 hours</option>
            <option value={3}>3 hours</option>
            <option value={4}>4 hours</option>
          </select>
        </div>
      )}

      <button
        onClick={savePrefs}
        disabled={saving}
        className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-lg font-medium text-sm transition-all ${
          saved ? 'bg-green-600 text-white' : 'bg-orange-500 hover:bg-orange-600 text-white'
        }`}
        data-testid="save-push-prefs-btn"
      >
        {saved ? (
          <><Save className="w-4 h-4" /> Saved!</>
        ) : saving ? (
          'Saving...'
        ) : (
          <><Save className="w-4 h-4" /> Save Preferences</>
        )}
      </button>
    </div>
  );
}
